import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AuthenticationContext } from '../../components/AuthenticationContext';
import Login from '../Login';
import { Box, Card, CardContent, CircularProgress, Grid, Tab, Tabs } from '@mui/material';
import { useQuery } from '@apollo/client';
import { GQL_GET_PLAYER } from '../../graphql/queries/players';

export default function PlayerView() {
  const { id, cameraId } = useParams<{ id: string; cameraId: string }>();

  const history = useHistory();
  const { authorized, removeToken } = useContext(AuthenticationContext);
  const [showCode, setShowCode] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cameraName, setCameraName] = useState();
  const [clientName, setClientName] = useState();
  const [playerInfo, setPlayerInfo] = useState<any>();
  const [paddingBottom, setPaddingBottom] = useState(56.25);
  const variables = { playerId: id };
  useQuery(GQL_GET_PLAYER, {
    onError(error) {
      if (error.graphQLErrors.findIndex((err) => err?.extensions?.code == 'UNAUTHENTICATED') != -1) {
        removeToken();
      } else {
        setError(`Failed to load data ${error.message}`);
        console.error(error);
      }
      setLoading(false);
    },
    onCompleted(data) {
      setLoading(false);
      if (data.player) {
        const positions = data.player.camera.panoramas.map(({ ref }: { ref: any }) => ref);
        data.player.camera.frameCaptures.forEach((capture: any) => {
          positions.push(capture.captures.map(({ ref }: { ref: any }) => ref));
        });
        setCameraName(data.player.camera.name);
        setClientName(data.player.camera.client.name);
        setPlayerInfo(data.player);
        if (data.player?.settings?.video?.aspectRatio)
          setPaddingBottom(Math.floor((100 * 100) / data.player.settings.video.aspectRatio) / 100);
      }
    },
    variables,
    skip: !authorized,
  });

  useEffect(() => {
    if (id) {
      const existingScript = document.getElementById('hdrelayScript');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = '//cdn10.hdrelay.com/js/hdrelay.js';
        script.id = 'hdrelayScript';
        document.body.appendChild(script);
      } else {
        const _window = window as any;
        if (_window.HDRelay?.create) _window.HDRelay.create({ target: '.hdrelay-player', id: id });
      }
    }
  }, [id]);

  if (!authorized) return <Login />;

  return (
    <Card>
      <CardContent>
        <Grid container alignItems="center" spacing={2}>
          <Grid container spacing={2} sx={{ margin: '0 0 20px 0' }} alignItems="center">
            <Grid item xs={12} sm>
              <h4 style={{ margin: 0 }}>
                <span style={{ cursor: 'pointer' }} onClick={() => history.push(`/`)}>
                  {clientName}
                </span>{' '}
                -{' '}
                <span style={{ cursor: 'pointer' }} onClick={() => history.push(`/camera/${cameraId}`)}>
                  {cameraName}
                </span>{' '}
              </h4>
              <h1 style={{ margin: 0 }}>{playerInfo?.name}</h1>
            </Grid>
          </Grid>
        </Grid>

        {loading && (
          <Box display="flex" minHeight="300px" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        )}
        {error && <h3>{error}</h3>}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className="hdrelay-player" style={{ width: '100%', margin: '0 auto' }} data-id={id}></div>
          </Grid>
          <Grid item xs={12}>
            <Tabs value={showCode} variant="fullWidth" onChange={(event, newValue) => setShowCode(newValue)}>
              <Tab label="Passive Loading Code" />
              <Tab label="Active Loading Code" />
              <Tab label="I-Frame Code" />
            </Tabs>
          </Grid>
          <Grid item xs={12}>
            {showCode == 0 && (
              <Box
                sx={{ border: 1, borderColor: 'divider', padding: '20px', fontFamily: 'monospace', fontSize: '14px' }}
              >
                <p style={{ fontStyle: 'italic' }}>
                  &lt;!-- Required libraries to be included preferentially in page head. --&gt;
                </p>

                <p>
                  &lt;script async src=&quot;//manage.hdrelay.com/js/hdrelay.js&quot;
                  type=&quot;text/javascript&quot;&gt;&lt;/script&gt;
                </p>

                <p style={{ fontStyle: 'italic' }}>
                  &lt;!-- Div that is going to hold web player. Load on DOM ready event. --&gt;
                </p>

                <p>
                  &lt;div style=&quot;width: 100%&quot; class=&quot;hdrelay-player&quot; data-id=&quot;{id}
                  &quot;&gt;&lt;/div&gt;
                </p>
              </Box>
            )}
            {showCode == 1 && (
              <Box
                sx={{ border: 1, borderColor: 'divider', padding: '20px', fontFamily: 'monospace', fontSize: '14px' }}
              >
                <p style={{ fontStyle: 'italic' }}>
                  &lt;!-- Required libraries to be included preferentially in page head. --&gt;
                </p>

                <p>
                  &lt;script src=&quot;//manage.hdrelay.com/js/hdrelay.js&quot;
                  type=&quot;text/javascript&quot;&gt;&lt;/script&gt;
                </p>

                <p style={{ fontStyle: 'italic' }}>
                  &lt;!-- Div that is going to hold web player. Size set to keep player space before load. --&gt;
                </p>

                <p>&lt;div style=&quot;width: 100%&quot; id=&quot;webcam_holder&quot;&gt;&lt;/div&gt;</p>

                <p style={{ fontStyle: 'italic' }}>&lt;!-- Code to show web player. Must go after div tag. --&gt;</p>

                <p>&lt;script type=&quot;text/javascript&quot;&gt;</p>

                <p>HDRelay.create(&#123;target:&quot;webcam_holder&quot;,id:&quot;{id}&quot;&#125;);</p>

                <p>&lt;/script&gt;</p>
              </Box>
            )}
            {showCode == 2 && (
              <Box
                sx={{ border: 1, borderColor: 'divider', padding: '20px', fontFamily: 'monospace', fontSize: '14px' }}
              >
                <p>
                  &lt;div style=&quot;width: 100%; height: 0; padding-bottom: {paddingBottom}%; padding-top: 27px;
                  position: relative;&quot;&gt;
                </p>
                <p>
                  &lt;iframe width=&quot;800&quot; height=&quot;474&quot; style=&quot;width: 100%; height: 100%;
                  position: absolute;&quot; src=&quot;https://manage.hdrelay.com/webPlayer/iframe/{id}&quot;
                  frameborder=&quot;0&quot; allow=&quot;autoplay; encrypted-media&quot;
                  allowfullscreen&gt;&lt;/iframe&gt;
                </p>
                <p>&lt;/div&gt;</p>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
